import { ClausesNote } from '../components/engagement/ClausesNote';

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EsignClientPanel } from '../components/esign/EsignClientPanel';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Trash2, FileText, Send, RefreshCw, Loader2, Download, Users } from 'lucide-react';
import { toast } from 'sonner';
import { clientsAPI } from '../lib/clientsAPI';
import {
  servicesEngagementAPI as API, BILLING_FREQUENCY_OPTIONS, LETTER_STATUS_META, fmtMoney, downloadLetterPdf,
} from '../lib/servicesEngagementAPI';

export default function ServicesAndFees() {
  const { id: clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [services, setServices] = useState([]);
  const [summary, setSummary] = useState({ subtotal: 0, gst: 0, discount: 0, total: 0 });
  const [letter, setLetter] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [includeMembers, setIncludeMembers] = useState([]);
  const [templateKey, setTemplateKey] = useState('auto');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [pickCat, setPickCat] = useState('');
  const [letterExtras, setLetterExtras] = useState({ payment_terms: '', special_conditions: '', custom_clauses: '' });

  const loadAll = useCallback(async () => {
    try {
      const [c, cat, sel, ltr, tpl, grp] = await Promise.all([
        clientsAPI.get(clientId).catch(() => ({ data: null })),
        API.listCatalogue().catch(() => ({ data: { services: [] } })),
        API.listSelected(clientId),
        API.getLetter(clientId),
        API.listTemplates().catch(() => ({ data: { templates: [] } })),
        API.getGroup(clientId).catch(() => ({ data: { members: [] } })),
      ]);
      setClient(c.data);
      setCatalogue((cat.data.services || []).filter((s) => s.active_status !== false));
      setServices(sel.data.services || []);
      setSummary(sel.data.summary || {});
      setLetter(ltr.data.letter);
      setTemplates(tpl.data.templates || []);
      setGroupMembers(grp.data.members || []);
      if (ltr.data.letter) {
        setLetterExtras({
          payment_terms: ltr.data.letter.payment_terms || '',
          special_conditions: ltr.data.letter.special_conditions || '',
          custom_clauses: ltr.data.letter.custom_clauses || '',
        });
        if (ltr.data.letter.template_key) setTemplateKey(ltr.data.letter.template_key);
        setIncludeMembers((ltr.data.letter.included_member_ids || []).filter((m) => m !== clientId));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const refreshSelected = async () => {
    const sel = await API.listSelected(clientId);
    setServices(sel.data.services || []);
    setSummary(sel.data.summary || {});
  };

  const addFromCatalogue = async () => {
    const item = catalogue.find((c) => c.id === pickCat);
    if (!item) { toast.error('Choose a service to add'); return; }
    setBusy(true);
    try {
      await API.addSelected(clientId, {
        service_catalogue_id: item.id, service_name: item.service_name,
        service_description: item.default_description, fee_amount: item.default_fee,
        billing_frequency: item.default_billing_frequency, gst_applicable: item.gst_applicable !== false,
      });
      setPickCat('');
      await refreshSelected();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
    finally { setBusy(false); }
  };

  const addCustom = async () => {
    setBusy(true);
    try {
      await API.addSelected(clientId, { service_name: 'Custom service', fee_amount: 0, billing_frequency: 'one_off', gst_applicable: true });
      await refreshSelected();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
    finally { setBusy(false); }
  };

  const patchService = async (svc, patch) => {
    try { await API.updateSelected(clientId, svc.id, patch); await refreshSelected(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const removeService = async (id) => {
    try { await API.removeSelected(clientId, id); await refreshSelected(); }
    catch (err) { toast.error('Failed to remove'); }
  };

  const toggleMember = (mid) => {
    setIncludeMembers((cur) => cur.includes(mid) ? cur.filter((x) => x !== mid) : [...cur, mid]);
  };

  const letterPayload = () => ({
    ...letterExtras,
    template_key: templateKey === 'auto' ? null : templateKey,
    include_group_members: includeMembers,
  });

  const generate = async () => {
    setBusy(true);
    try {
      const res = await API.generate(clientId, letterPayload());
      setLetter(res.data);
      if (res.data.template_key) setTemplateKey(res.data.template_key);
      toast.success(`Engagement letter generated — ${res.data.template_name || 'template applied'}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate'); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    if (!letter) return;
    setBusy(true);
    try {
      const res = await API.editLetter(letter.id, { ...letterPayload(), regenerate: true });
      setLetter(res.data);
      if (res.data.template_key) setTemplateKey(res.data.template_key);
      toast.success('Letter updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!letter) return;
    setBusy(true);
    try { const res = await API.send(letter.id); setLetter(res.data); toast.success('Sent to client for signing'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!letter) return;
    try { await downloadLetterPdf(letter.id, letter.engagement_letter_number); }
    catch (err) { toast.error('Failed to download PDF'); }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;
  const locked = letter && ['signed', 'stored'].includes(letter.status);
  const entityOptions = [{ id: clientId, display_name: client?.display_name || 'This client' }, ...groupMembers];

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate(`/clients/${clientId}`)} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to client
      </Button>
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Services, Fees &amp; Engagement Letter</h1>
        <p className="text-muted-foreground mt-1">{client?.display_name}</p>
      </div>

      <EsignClientPanel clientId={clientId} />

      {/* Services Provided */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Services Provided</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[220px] space-y-1">
              <Label className="text-xs">Add from catalogue</Label>
              <Select value={pickCat} onValueChange={setPickCat}>
                <SelectTrigger data-testid="cat-pick"><SelectValue placeholder="Choose a service…" /></SelectTrigger>
                <SelectContent>
                  {catalogue.map((c) => <SelectItem key={c.id} value={c.id}>{c.service_name} — {fmtMoney(c.default_fee)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={addFromCatalogue} disabled={busy || locked} data-testid="add-service"><Plus className="w-4 h-4 mr-1" /> Add</Button>
            <Button variant="outline" onClick={addCustom} disabled={busy || locked}>Add custom</Button>
          </div>

          {services.length === 0 ? (
            <p className="text-sm text-muted-foreground/70 py-4 text-center">No services selected yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Service</TableHead>
                    {groupMembers.length > 0 && <TableHead>For entity</TableHead>}
                    <TableHead>Billing</TableHead><TableHead>Fee (ex GST)</TableHead>
                    <TableHead>Discount</TableHead><TableHead>GST</TableHead><TableHead>Total</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {services.map((s) => (
                    <TableRow key={s.id} data-testid={`svc-row-${s.id}`}>
                      <TableCell>
                        <Input className="h-8 w-40" defaultValue={s.service_name} disabled={locked}
                          onBlur={(e) => e.target.value !== s.service_name && patchService(s, { service_name: e.target.value })} />
                      </TableCell>
                      {groupMembers.length > 0 && (
                        <TableCell>
                          <Select value={s.related_entity_id || clientId} onValueChange={(v) => patchService(s, { related_entity_id: v })} disabled={locked}>
                            <SelectTrigger className="h-8 w-36" data-testid={`svc-entity-${s.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{entityOptions.map((m) => <SelectItem key={m.id} value={m.id}>{m.display_name}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                      )}
                      <TableCell>
                        <Select value={s.billing_frequency} onValueChange={(v) => patchService(s, { billing_frequency: v })} disabled={locked}>
                          <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>{BILLING_FREQUENCY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input type="number" className="h-8 w-24" defaultValue={s.fee_amount} disabled={locked}
                          onBlur={(e) => Number(e.target.value) !== s.fee_amount && patchService(s, { fee_amount: Number(e.target.value) })} />
                      </TableCell>
                      <TableCell>
                        <Input type="number" className="h-8 w-20" defaultValue={s.discount_amount} disabled={locked}
                          onBlur={(e) => Number(e.target.value) !== s.discount_amount && patchService(s, { discount_amount: Number(e.target.value) })} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtMoney(s.gst_amount)}</TableCell>
                      <TableCell className="text-sm font-medium">{fmtMoney(s.total_amount)}</TableCell>
                      <TableCell>
                        {!locked && <Button size="sm" variant="ghost" onClick={() => removeService(s.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Fee summary */}
          <div className="flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmtMoney(summary.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">GST</span><span>{fmtMoney(summary.gst)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>-{fmtMoney(summary.discount)}</span></div>
              <div className="flex justify-between font-semibold text-foreground border-t pt-1"><span>Total payable</span><span>{fmtMoney(summary.total)}</span></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Combined group letter */}
      {groupMembers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> Combined Group Letter</CardTitle>
            <CardDescription>Include related entities to issue one engagement letter covering the whole group. Services can be assigned to each entity in the table above.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {groupMembers.map((m) => (
              <label key={m.id} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50">
                <Checkbox checked={includeMembers.includes(m.id)} onCheckedChange={() => toggleMember(m.id)}
                  disabled={locked} data-testid={`group-member-${m.id}`} />
                <div>
                  <span className="text-sm font-medium text-foreground">{m.display_name}</span>
                  <span className="text-xs text-muted-foreground ml-2 capitalize">
                    {(m.entity_type || 'entity').replace('_', ' ')} · {(m.relationship_type || 'related').replace('_', ' ')}
                  </span>
                </div>
              </label>
            ))}
            {includeMembers.length > 0 && (
              <p className="text-xs text-primary">The Combined Group template will be used automatically unless you pick a different template below.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Engagement letter */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Engagement Letter</CardTitle>
            <CardDescription>
              {letter ? (
                <>Ref {letter.engagement_letter_number}
                  {letter.template_name ? <> · {letter.template_name}</> : null}
                  {' · '}<Badge className={(LETTER_STATUS_META[letter.status] || {}).color}>{(LETTER_STATUS_META[letter.status] || {}).label || letter.status}</Badge></>
              ) : 'Generate an IPA-branded letter from the selected services.'}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!locked && (
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Letter template</Label>
                <Select value={templateKey} onValueChange={setTemplateKey}>
                  <SelectTrigger data-testid="letter-template-pick"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto — pick the best template for this client</SelectItem>
                    {templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {templateKey !== 'auto' && (
                  <p className="text-xs text-muted-foreground">{(templates.find((t) => t.key === templateKey) || {}).description}</p>
                )}
              </div>
              <div className="space-y-1"><Label className="text-xs">Payment terms</Label><Input value={letterExtras.payment_terms} onChange={(e) => setLetterExtras((x) => ({ ...x, payment_terms: e.target.value }))} placeholder="Default applies if left blank" /></div>
              <div className="space-y-1"><Label className="text-xs">Special conditions</Label><Textarea rows={2} value={letterExtras.special_conditions} onChange={(e) => setLetterExtras((x) => ({ ...x, special_conditions: e.target.value }))} /></div>
              <div className="space-y-1"><Label className="text-xs">Custom clauses</Label><Textarea rows={2} value={letterExtras.custom_clauses} onChange={(e) => setLetterExtras((x) => ({ ...x, custom_clauses: e.target.value }))} /></div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {!letter && <Button onClick={generate} disabled={busy || services.length === 0} data-testid="generate-letter"><FileText className="w-4 h-4 mr-1" /> Generate engagement letter</Button>}
            {letter && !locked && <Button variant="outline" onClick={regenerate} disabled={busy}><RefreshCw className="w-4 h-4 mr-1" /> Regenerate from services</Button>}
            {letter && !locked && <Button onClick={send} disabled={busy} data-testid="send-letter"><Send className="w-4 h-4 mr-1" /> Send to client</Button>}
            {letter && <Button variant="outline" onClick={downloadPdf} data-testid="download-letter-pdf"><Download className="w-4 h-4 mr-1" /> Download PDF</Button>}
          </div>

          {letter?.letter_content && <ClausesNote state={letter.clauses_state} />}
          {letter?.letter_content && (
            <div className="border rounded-lg p-4 bg-muted/50 max-h-[480px] overflow-y-auto" data-testid="letter-content-view">
              <pre className="whitespace-pre-wrap text-xs text-foreground/90 font-sans">{letter.letter_content}</pre>
            </div>
          )}
          {letter?.status === 'needs_changes' && letter?.client_comment && (
            <div className="text-sm bg-orange-50 border border-orange-200 rounded-lg p-3 text-orange-800">
              Client requested changes: “{letter.client_comment}”
            </div>
          )}
          {locked && <p className="text-sm text-green-700">Signed by {letter.signed_by_name} — this letter is locked.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
